import React, { useState, useEffect, useContext } from "react";
import { Tab, Button, Icon } from "rbx";
import moment from "moment";
import { Link, Switch, Route, useLocation } from "react-router-dom";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { isEmpty } from "lodash";
import { VehicleColumns } from "../Vehicles/columns";
import { ALL_VEHICLES_VIEW_BY_PAGE_QUERY } from "../../../graphql/Vehicles/Vehicles.graphql";

import DataTable from "../../../components/DataTable";
import { ModalContext } from "../../../components/ModalProvider";
import { useSystemCodes } from "../../../utils/hooks";

import PageHeader from "../../../components/PageHeader";
import VehicleOutOfServiceFilters from "./VehicleOutOfServiceFilters";
import { getFilterState, saveFilterState } from "../../../utils";
import { VehicleOutOfServiceModal } from "../../../components/VehicleOutOfService";

const DEFAULT_FILTERS = {
  VehicleNumber_contains: "",
  VehicleTypeRecIdRef: null
};

const VehicleOutOfServicesPage = () => {
  const location = useLocation();
  const { setModalOpen } = useContext(ModalContext);
  const INITIAL_FILTER_STATE = getFilterState(
    "VEHICLE_OUT_OF_SERVICES_FILTERS",
    {
      ...DEFAULT_FILTERS
    }
  );
  const SystemCodes = useSystemCodes({
    Category: "Fleet",
    CodeName: "VehicleStatus"
  });

  const [FiltersCodes, setFiltersCodes] = useState(null);
  const [filters, setFilters] = useState({});

  useEffect(() => {
    if (SystemCodes?.data?.length > 0 && !FiltersCodes) {
      setFiltersCodes({
        Available: SystemCodes.data.find(s => s.CodeId === "A"),
        Inactive: SystemCodes.data.find(s => s.CodeId === "I"),
        InShopService: SystemCodes.data.find(s => s.CodeId === "O"),
        Terminated: SystemCodes.data.find(s => s.CodeId === "T"),
        VendorService: SystemCodes.data.find(s => s.CodeId === "VS"),
        OffsiteStorage: SystemCodes.data.find(s => s.CodeId === "OS")
      });
    }
    // Setting up the default filters on start
    if (!isEmpty(filters)) {
      const Filters = {};
      Object.keys(INITIAL_FILTER_STATE).forEach(
        key =>
          (Filters[key] =
            INITIAL_FILTER_STATE[key] && INITIAL_FILTER_STATE[key].value
              ? INITIAL_FILTER_STATE[key].value
              : INITIAL_FILTER_STATE[key])
      );
      if (Filters.VehicleTypeRecIdRef) {
        Filters.VehicleTypeRecIdRef = parseInt(Filters.VehicleTypeRecIdRef);
      }
      setFilters({
        ...Filters
      });
    }
  }, [FiltersCodes, INITIAL_FILTER_STATE, SystemCodes, filters]);

  const handleSetFilters = inputs => {
    // Clearing filters if is empty and avoid null response from query
    Object.keys(inputs).forEach(
      key =>
        (!inputs[key] || inputs[key] === 0 || inputs[key] === "") &&
        delete inputs[key]
    );

    // Seting up the Filters values
    const Filters = {};
    Object.keys(inputs).forEach(
      key =>
        (Filters[key] =
          inputs[key] && inputs[key].value ? inputs[key].value : inputs[key])
    );
    if (Filters.VehicleTypeRecIdRef) {
      Filters.VehicleTypeRecIdRef = parseInt(Filters.VehicleTypeRecIdRef);
    }
    setFilters(Filters);
    saveFilterState("VEHICLE_OUT_OF_SERVICES_FILTERS", Filters);
  };

  const resetFilters = () =>
    setFilters({
      ...DEFAULT_FILTERS
    });

  const editRow = obj => {
    const item = obj.data[obj.row.id];
    let tabActive = null;
    tabActive =
      location.pathname.includes(
        "fleet/vehicle-status-processing/offsite-storage"
      ) && "OffsiteStorage";
    tabActive =
      tabActive ||
      (location.pathname.includes(
        "fleet/vehicle-status-processing/available"
      ) &&
        "Available");
    tabActive =
      tabActive ||
      (location.pathname.includes("fleet/vehicle-status-processing/inactive") &&
        "Inactive");
    tabActive =
      tabActive ||
      (location.pathname.includes(
        "fleet/vehicle-status-processing/in-shop-service"
      ) &&
        "InShopService");
    tabActive =
      tabActive ||
      (location.pathname.includes(
        "fleet/vehicle-status-processing/terminated"
      ) &&
        "Terminated");
    tabActive =
      tabActive ||
      (location.pathname.includes(
        "fleet/vehicle-status-processing/vendor-service"
      ) &&
        "VendorService");
    return (
      <Button
        disabled={
          parseInt(item.StatusRecIdRef) ===
          parseInt(FiltersCodes.Terminated.RecId)
        }
        size="small"
        type="button"
        onClick={e => {
          e.stopPropagation();
          e.preventDefault();
          setModalOpen(
            true,
            <VehicleOutOfServiceModal
              {...{
                vehicleId: item.RecId,
                onComplete: () => setModalOpen(false),
                excludedStatus: FiltersCodes[tabActive].RecId
              }}
            />
          );
        }}
      >
        <span>Update Status</span>
        <Icon>
          <FontAwesomeIcon icon="edit" />
        </Icon>
      </Button>
    );
  };
  const parseFilters = input => {
    const { ...where } = input;
    if (input.CreatedDateTime_gte) {
      where.CreatedDateTime_gte = moment(input.CreatedDateTime_gte)
        .startOf("day")
        .toISOString();
    }
    if (input.CreatedDateTime_lte) {
      where.CreatedDateTime_lte = moment(input.CreatedDateTime_lte)
        .endOf("day")
        .toISOString();
    }

    return where;
  };
  return (
    <div>
      <PageHeader
        FilterComponent={VehicleOutOfServiceFilters}
        columns={VehicleColumns(editRow, false)}
        filters={parseFilters(filters)}
        name="VW_VehiclesByPage"
        query={ALL_VEHICLES_VIEW_BY_PAGE_QUERY}
        resetFilters={resetFilters}
        setFilters={handleSetFilters}
        title="Vehicle Status Processing"
        onAddClick={() =>
          setModalOpen(
            true,
            <VehicleOutOfServiceModal
              {...{
                id: null,
                onComplete: () => setModalOpen(false)
              }}
            />
          )
        }
      />
      <Tab.Group kind="boxed">
        <Tab
          active={location.pathname.includes(
            "fleet/vehicle-status-processing/available"
          )}
          as={Link}
          tabIndex="-1"
          to="/fleet/vehicle-status-processing/available"
        >
          <span>Available</span>
        </Tab>
        <Tab
          active={location.pathname.includes(
            "fleet/vehicle-status-processing/inactive"
          )}
          as={Link}
          tabIndex="-1"
          to="/fleet/vehicle-status-processing/inactive"
        >
          <span>Inactive</span>
        </Tab>
        <Tab
          active={location.pathname.includes(
            "fleet/vehicle-status-processing/in-shop-service"
          )}
          as={Link}
          tabIndex="-1"
          to="/fleet/vehicle-status-processing/in-shop-service"
        >
          <span>In-shop Service</span>
        </Tab>
        <Tab
          active={location.pathname.includes(
            "fleet/vehicle-status-processing/terminated"
          )}
          as={Link}
          tabIndex="-1"
          to="/fleet/vehicle-status-processing/terminated"
        >
          <span>Terminated</span>
        </Tab>
        <Tab
          active={location.pathname.includes(
            "fleet/vehicle-status-processing/vendor-service"
          )}
          as={Link}
          tabIndex="-1"
          to="/fleet/vehicle-status-processing/vendor-service"
        >
          <span>Vendor Service</span>
        </Tab>
        <Tab
          active={location.pathname.includes(
            "fleet/vehicle-status-processing/offsite-storage"
          )}
          as={Link}
          tabIndex="-1"
          to="/fleet/vehicle-status-processing/offsite-storage"
        >
          <span>Offsite Storage</span>
        </Tab>
      </Tab.Group>
      <Switch>
        <Route path="/fleet/vehicle-status-processing/available">
          {FiltersCodes && FiltersCodes.Available && (
            <DataTable
              isPaginated
              columns={VehicleColumns(editRow, false)}
              dataQuery={ALL_VEHICLES_VIEW_BY_PAGE_QUERY}
              initialSortBy={[
                { id: "VehicleNumber", desc: false },
                { id: "CreatedDateTime", desc: true }
              ]}
              name="VW_VehiclesByPage"
              where={parseFilters({
                ...filters,
                StatusCodeDescription: FiltersCodes.Available.CodeDescription
              })}
              onRowClick={({ RecId }) =>
                setModalOpen(
                  true,
                  <VehicleOutOfServiceModal
                    {...{
                      vehicleId: RecId,
                      onComplete: () => setModalOpen(false)
                    }}
                  />
                )
              }
            />
          )}
        </Route>

        <Route path="/fleet/vehicle-status-processing/inactive">
          {FiltersCodes && FiltersCodes.Inactive && (
            <DataTable
              isPaginated
              columns={VehicleColumns(editRow, false)}
              dataQuery={ALL_VEHICLES_VIEW_BY_PAGE_QUERY}
              initialSortBy={[
                { id: "VehicleNumber", desc: false },
                { id: "CreatedDateTime", desc: true }
              ]}
              name="VW_VehiclesByPage"
              where={parseFilters({
                ...filters,
                StatusCodeDescription: FiltersCodes.Inactive.CodeDescription
              })}
              onRowClick={({ RecId }) =>
                setModalOpen(
                  true,
                  <VehicleOutOfServiceModal
                    {...{
                      vehicleId: RecId,
                      onComplete: () => setModalOpen(false)
                    }}
                  />
                )
              }
            />
          )}
        </Route>
        <Route path="/fleet/vehicle-status-processing/in-shop-service">
          {FiltersCodes && FiltersCodes.InShopService && (
            <DataTable
              isPaginated
              columns={VehicleColumns(editRow, false)}
              dataQuery={ALL_VEHICLES_VIEW_BY_PAGE_QUERY}
              initialSortBy={[
                { id: "VehicleNumber", desc: false },
                { id: "CreatedDateTime", desc: true }
              ]}
              name="VW_VehiclesByPage"
              where={parseFilters({
                ...filters,
                StatusCodeDescription:
                  FiltersCodes.InShopService.CodeDescription
              })}
              onRowClick={({ RecId }) =>
                setModalOpen(
                  true,
                  <VehicleOutOfServiceModal
                    {...{
                      vehicleId: RecId,
                      onComplete: () => setModalOpen(false)
                    }}
                  />
                )
              }
            />
          )}
        </Route>
      </Switch>

      <Route path="/fleet/vehicle-status-processing/terminated">
        {FiltersCodes && FiltersCodes.Terminated && (
          <DataTable
            isPaginated
            columns={VehicleColumns(editRow, true)}
            dataQuery={ALL_VEHICLES_VIEW_BY_PAGE_QUERY}
            initialSortBy={[
              { id: "VehicleNumber", desc: false },
              { id: "CreatedDateTime", desc: true }
            ]}
            name="VW_VehiclesByPage"
            where={parseFilters({
              ...filters,
              StatusCodeDescription: FiltersCodes.Terminated.CodeDescription
            })}
            onRowClick={({ RecId }) =>
              setModalOpen(
                true,
                <VehicleOutOfServiceModal
                  {...{
                    vehicleId: RecId,
                    onComplete: () => setModalOpen(false)
                  }}
                />
              )
            }
          />
        )}
      </Route>
      <Route path="/fleet/vehicle-status-processing/vendor-service">
        {FiltersCodes && FiltersCodes.VendorService && (
          <DataTable
            isPaginated
            columns={VehicleColumns(editRow, false)}
            dataQuery={ALL_VEHICLES_VIEW_BY_PAGE_QUERY}
            initialSortBy={[
              { id: "VehicleNumber", desc: false },
              { id: "CreatedDateTime", desc: true }
            ]}
            name="VW_VehiclesByPage"
            where={parseFilters({
              ...filters,
              StatusCodeDescription: FiltersCodes.VendorService.CodeDescription
            })}
            onRowClick={({ RecId }) =>
              setModalOpen(
                true,
                <VehicleOutOfServiceModal
                  {...{
                    vehicleId: RecId,
                    onComplete: () => setModalOpen(false)
                  }}
                />
              )
            }
          />
        )}
      </Route>
      <Route path="/fleet/vehicle-status-processing/offsite-storage">
        {FiltersCodes && FiltersCodes.OffsiteStorage && (
          <DataTable
            isPaginated
            columns={VehicleColumns(editRow, false)}
            dataQuery={ALL_VEHICLES_VIEW_BY_PAGE_QUERY}
            initialSortBy={[
              { id: "VehicleNumber", desc: false },
              { id: "CreatedDateTime", desc: true }
            ]}
            name="VW_VehiclesByPage"
            where={parseFilters({
              ...filters,
              StatusCodeDescription: FiltersCodes.OffsiteStorage.CodeDescription
            })}
            onRowClick={({ RecId }) =>
              setModalOpen(
                true,
                <VehicleOutOfServiceModal
                  {...{
                    vehicleId: RecId,
                    onComplete: () => setModalOpen(false)
                  }}
                />
              )
            }
          />
        )}
      </Route>
    </div>
  );
};

VehicleOutOfServicesPage.propTypes = {};

export default VehicleOutOfServicesPage;
